import React, { useMemo, useState, FC } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { BreakRule } from 'repository/timekeeper/@types/BreakRuleModel';
import { BreakRuleDialog } from 'components/dialog/break_rule/BreakRuleDialog';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplayIcon from '@mui/icons-material/Replay';
import { buildSettingsBasePath, buildSettingsCrudPath, parseSettingsCrudRoute } from 'utils/settingsCrudRouting';

interface ContextMenuPosition {
    mouseX: number;
    mouseY: number;
}

interface ContextMenuProps {
    contextMenu: ContextMenuPosition | null;
    setContextMenu: (value: ContextMenuPosition | null) => void;
    selectedRow: BreakRule[];
    reloading: () => void;
    cleanSelected: () => void;
    rows: BreakRule[];
}

export const ContextMenu: FC<ContextMenuProps> = ({ selectedRow, cleanSelected, rows, contextMenu, setContextMenu, reloading }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [localOpen, setLocalOpen] = useState(false);
    const [localMode, setLocalMode] = useState<'add' | 'edit' | 'delete' | 'info' | 'duplicate'>('add');

    const basePath = buildSettingsBasePath('break_rules');
    const modalRoute = useMemo(() => parseSettingsCrudRoute(location.pathname, 'break_rules'), [location.pathname]);
    const routeOpen = modalRoute.action === 'add' || (modalRoute.action === 'update' && modalRoute.id !== null) || (modalRoute.action === 'detail' && modalRoute.id !== null);
    const routeMode: 'add' | 'edit' | 'info' = modalRoute.action === 'add' ? 'add' : modalRoute.action === 'detail' ? 'info' : 'edit';
    const open = routeOpen || localOpen;
    const mode = routeOpen ? routeMode : localMode;

    const selectedRowFromRoute = useMemo(() => {
        if (modalRoute.id === null) return null;
        return rows.find((row) => Number(row.id) === modalRoute.id) || null;
    }, [rows, modalRoute.id]);
    const selectedRowsForDialog = selectedRowFromRoute ? [selectedRowFromRoute] : selectedRow;

    const closeDialog = () => {
        if (routeOpen) {
            navigate(basePath);
        } else {
            setLocalOpen(false);
        }
        cleanSelected();
    };

    const options = [
        {
            name: t('settings.holidays.table.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSettingsCrudPath('break_rules', 'add'));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: false,
            selected: false,
            onlyOne: false,
            hidden: (JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.CreateHoliday') ? false : true,
        },
        {
            name: t('settings.holidays.table.context_menu.info'),
            icon: <VisibilityIcon />,
            click: () => {
                const current = selectedRow[0];
                if (!current?.id) return;
                navigate(buildSettingsCrudPath('break_rules', 'detail', current.id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selectedRow.length <= 0 ? true : false,
            selected: true,
            onlyOne: false,
            hidden:
                selectedRow.length <= 0 || !(JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.HolidayById') ? true : false,
        },
        {
            name: t('settings.holidays.table.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                const current = selectedRow[0];
                if (!current?.id) return;
                navigate(buildSettingsCrudPath('break_rules', 'update', current.id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selectedRow.length === 1 ? false : true,
            selected: false,
            onlyOne: true,
            hidden:
                selectedRow.length !== 1 || !(JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.PatchHolidayById')
                    ? true
                    : false,
        },
        {
            name: t('settings.holidays.table.context_menu.duplicate'),
            icon: <ContentCopyIcon />,
            click: () => {
                setLocalMode('duplicate');
                setLocalOpen(true);
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selectedRow.length === 1 ? false : true,
            selected: false,
            onlyOne: true,
            hidden:
                selectedRow.length !== 1 || !(JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.CreateHoliday') ? true : false,
        },
        {
            name: t('settings.holidays.table.context_menu.delete'),
            icon: <DeleteIcon color="error" />,
            click: () => {
                setLocalMode('delete');
                setLocalOpen(true);
                setContextMenu(null);
            },
            color: 'error',
            divider: false,
            disabled: true,
            selected: true,
            onlyOne: false,
            hidden:
                selectedRow.length === 0 || !(JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.DeleteHolidayById')
                    ? true
                    : false,
        },
        {
            name: t('settings.holidays.table.context_menu.reload'),
            icon: <ReplayIcon />,
            click: () => {
                reloading();
                setContextMenu(null);
            },
            color: '',
            divider: true,
            disabled: false,
            selected: false,
            onlyOne: false,
            hidden: false,
        },
    ];

    return (
        <>
            <Menu
                open={Boolean(contextMenu)}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
            >
                {options.map((option, index) => (
                    <div key={option.name} hidden={option.onlyOne && selectedRow.length !== 1}>
                        {option.divider && <Divider />}
                        <MenuItem onClick={option.click} disabled={option.disabled}>
                            <ListItemIcon>{option.icon}</ListItemIcon>
                            <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />
                        </MenuItem>
                    </div>
                ))}
            </Menu>
            <BreakRuleDialog open={open} setOpen={setLocalOpen} onClose={closeDialog} rows={rows} selectedRow={selectedRowsForDialog} reloading={reloading} mode={mode} />
        </>
    );
};
